
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { affiliateService, AffiliateService } from '@/services/affiliateService';
import { logger } from '@/utils/production-logger';

/**
 * Custom hook to check if the current user has an affiliate record.
 * Returns true if the user has any affiliate record (pending, active, or suspended).
 */
export const useIsActiveAffiliate = () => {
  const { user } = useAuth();
  
  logger.debug('useIsActiveAffiliate - User from auth', {
    component: 'useIsActiveAffiliate',
    userId: user?.id,
    metadata: {
      userEmail: user?.email,
      userExists: !!user
    }
  });
  
  const { data: affiliateInfo, isLoading, error } = useQuery({
    queryKey: ['affiliateByUserId', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        logger.debug('No user ID available for affiliate check', {
          component: 'useIsActiveAffiliate',
          action: 'queryFn'
        });
        return null;
      }
      
      logger.debug('Calling AffiliateService.getAffiliateByUserId', {
        component: 'useIsActiveAffiliate',
        action: 'queryFn',
        userId: user.id
      });
      
      try {
        // Use the static method directly from the AffiliateService class
        const result = await AffiliateService.getAffiliateByUserId(user.id);
        
        logger.debug('AffiliateService.getAffiliateByUserId result', {
          component: 'useIsActiveAffiliate',
          action: 'queryFn',
          metadata: {
            hasResult: !!result,
            affiliateId: result?.id,
            status: result?.status
          }
        });
        
        return result;
      } catch (err) {
        logger.error('Error in AffiliateService.getAffiliateByUserId', {
          component: 'useIsActiveAffiliate',
          action: 'queryFn',
          userId: user.id
        }, err as Error);
        throw err;
      }
    },
    enabled: !!user?.id,
    retry: false, // Don't retry if user doesn't have affiliate record
  });

  logger.debug('useIsActiveAffiliate - Query state', {
    component: 'useIsActiveAffiliate',
    metadata: {
      isLoading,
      hasError: !!error,
      hasAffiliateInfo: !!affiliateInfo
    }
  });

  // User has affiliate record if no error and data exists
  const hasAffiliateRecord = !!affiliateInfo && !error;
  
  // Consider user an active affiliate only if they have status === 'active'
  const isActiveAffiliate = hasAffiliateRecord && affiliateInfo?.status === 'active';
  
  logger.debug('useIsActiveAffiliate - Final results', {
    component: 'useIsActiveAffiliate',
    metadata: {
      hasAffiliateRecord,
      isActiveAffiliate,
      affiliateStatus: affiliateInfo?.status,
      isLoading
    }
  });
  
  return {
    hasAffiliateRecord,
    isActiveAffiliate,
    isLoading,
    affiliateStatus: affiliateInfo?.status
  };
};
